import React from 'react';
import { uploadDecision } from '../../decisionUpload.api';
import { DecisionUploadCityField } from './city/DecisionUploadCityField';
import { DecisionUploadDateField } from './date/DecisionUploadDateField';
import { DecisionUploadDayField } from './date/DecisionUploadDayField';
import { DecisionUploadError } from './error/DecisionUploadError';
import { DecisionUploadFileField } from './file/DecisionUploadFileField';
import { DecisionUploadJuridictionField } from './jurisdiction/DecisionUploadJuridictionField';
import { DecisionUploadMonthField } from './date/DecisionUploadMonthField';
import { DecisionUploadNumberField } from './number/DecisionUploadNumberField';
import { DecisionUploadYearField } from './date/DecisionUploadYearField';
import { doctrineAnalytics } from '../../../../utils/analyticsUtils';
import {
  getErrors,
  getDataFormated,
  shouldHaveCity,
} from './decisionUploadUtils';

type Props = {
  handleSuccess: Function;
  jurisdictionsKeys: string[];
};

type State = {
  values: {
    juridiction: string;
    city: string;
    number: string;
    day: string;
    month: string;
    year: string;
    file: File | undefined;
  };
  isSubmitted: boolean;
  isSubmitting: boolean;
  errorType?: 'extension' | 'other';
};

const defaultErrors: { [s: string]: Array<string> } = {
  juridiction: [],
  city: [],
  number: [],
  date: [],
  file: [],
  day: [],
  month: [],
  year: [],
};

export class DecisionUploadForm extends React.Component<Props, State> {
  state: State = {
    isSubmitted: false,
    isSubmitting: false,
    values: {
      juridiction: '',
      city: '',
      number: '',
      day: '',
      month: '',
      year: '',
      file: undefined,
    },
    errorType: undefined,
  };

  onChange = (
    event: { target: HTMLInputElement } | { target: HTMLSelectElement },
  ) => {
    const { name, value } = event.target;
    this.setState(state => ({
      values: {
        ...state.values,
        [name]: value,
      },
    }));
  };

  handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    const { jurisdictionsKeys } = this.props;
    event.preventDefault();
    this.setState({ isSubmitted: true }, async () => {
      const { values } = this.state;
      const errors = getErrors(values, jurisdictionsKeys);
      const { handleSuccess } = this.props;
      this.setState({ errorType: undefined });
      const errorsArrays = Object.values(errors);

      if (errorsArrays.every(item => item.length === 0)) {
        this.setState({ isSubmitting: true });
        doctrineAnalytics('Upload FileSent');
        try {
          const data = getDataFormated(values, jurisdictionsKeys);
          await uploadDecision(data);
          handleSuccess(true);
        } catch (error) {
          this.setState({
            isSubmitting: false,
            errorType:
              error.data && error.data.error === 'INVALID_EXT_DECISION_UPLOAD'
                ? 'extension'
                : 'other',
          });
        }
      }
    });
  };

  handleFileDrop = (accepted: Array<File>, rejectedFiles: Array<File>) => {
    const file = accepted[0] || rejectedFiles[0];
    const { values } = this.state;
    const newValues = { ...values };
    newValues.file = file;

    this.setState({ values: newValues });
  };

  resetFile = () => {
    this.setState(state => ({
      values: {
        ...state.values,
        file: undefined,
      },
    }));
  };

  render() {
    const { values, isSubmitting, isSubmitted, errorType } = this.state;
    const { jurisdictionsKeys } = this.props;
    let errors = defaultErrors;
    if (isSubmitted) {
      errors = getErrors(values, jurisdictionsKeys);
    }
    const isFutureDate = errors.date.includes('FUTURE_DATE');
    return (
      <form onSubmit={this.handleSubmit}>
        <DecisionUploadJuridictionField
          value={values.juridiction}
          onChange={this.onChange}
          hasError={errors.juridiction.length > 0}
        />
        {shouldHaveCity(values.juridiction, jurisdictionsKeys) && (
          <DecisionUploadCityField
            value={values.city}
            onChange={this.onChange}
            hasError={errors.city.length > 0}
          />
        )}
        <DecisionUploadNumberField
          value={values.number}
          onChange={this.onChange}
          hasError={errors.number.length > 0}
        />
        <DecisionUploadDateField errors={errors.date}>
          <DecisionUploadDayField
            value={values.day}
            onChange={this.onChange}
            hasError={errors.day.length > 0 || isFutureDate}
          />
          <DecisionUploadMonthField
            value={values.month}
            onChange={this.onChange}
            hasError={errors.month.length > 0 || isFutureDate}
          />
          <DecisionUploadYearField
            value={values.year}
            onChange={this.onChange}
            hasError={errors.year.length > 0 || isFutureDate}
          />
        </DecisionUploadDateField>
        <DecisionUploadFileField
          value={values.file}
          handleFileDrop={this.handleFileDrop}
          resetFile={this.resetFile}
          hasError={errors.file.length > 0}
        />

        {errorType && <DecisionUploadError type={errorType} />}
        <button
          className="button brand large expanded upload__message__button"
          type="submit"
          disabled={isSubmitting}
        >
          {errorType ? "Réessayer l'envoi d'une décision" : 'Envoyer'}
        </button>
      </form>
    );
  }
}
